import { useError } from "../../context/useErrorContext"
import { useLoad } from "../../context/useLoadContext"
import { useGetCompany } from "./useGetCompany"
import { CompanyEntity } from "../../Objects/Company/CompanyEntity"
import { API_ROUTES } from "../../configuration/RoutesApi"
import { PostFormRequest, PutFormRequest, PutRequest } from "../../services/ApiClientService"
import { useTranslation } from "react-i18next"
import { OperatingScheduleEntity } from "../../Objects/OperatingSchedule/OperatingScheduleEntity"
import { ERestaurantServiceType } from "../../Enum/ERestaurantServiceType"

export const useSaveCompany = () => {
  const { t } = useTranslation("general")

  const { enableLoad, disableLoad } = useLoad()

  const { setError } = useError()

  const { handleGetCompany } = useGetCompany()

  const handleSaveCompany = async (company: CompanyEntity, onSuccess: any, imageFile?: any | null) => {
    try {
      enableLoad()

      var response = null

      if (company.id !== -1) {
        response = await PutFormRequest(`${API_ROUTES.COMPANY.PUT}/${company.id}`, company.toJSON(imageFile))
      } else {
        response = await PostFormRequest(API_ROUTES.COMPANY.CREATE, company.toJSON(imageFile))
      }

      if (response.success) {
        await handleGetCompany(onSuccess)

        return true
      }

      throw new Error(response.message)
    } catch (e: any) {
      const message = e instanceof Error ? e.message : t("label_unknown_error_description")
      setError(t("label_title_erro"), message)

      return false
    } finally {
      disableLoad()
    }
  }

  const handleSaveTableSelfService = async (
    companyId: number,
    isEnabled: boolean,
    isOrderingEnabled: boolean,
    onSuccess: () => void,
  ) => {
    try {
      enableLoad()

      const response = await PutRequest(`${API_ROUTES.COMPANY.UPDATE_TABLE_SELF_SERVICE}/${companyId}`, {
        isTableSelfServiceEnabled: isEnabled,
        isTableSelfServiceOrderingEnabled: isEnabled && isOrderingEnabled,
      })

      if (!response.success) {
        throw new Error(response.message)
      }

      await handleGetCompany(onSuccess)
      return true
    } catch (e) {
      setError(t("label_title_erro"), e instanceof Error ? e.message : t("label_unknown_error_description"))
      return false
    } finally {
      disableLoad()
    }
  }

  const handleSaveDefaultTable = async (companyId: number, defaultTableId: number | null) => {
    try {
      enableLoad()

      const response = await PutRequest(`${API_ROUTES.COMPANY.UPDATE_DEFAULT_TABLE}/${companyId}`, {
        defaultTableId,
      })

      if (!response.success) {
        throw new Error(response.message)
      }

      await handleGetCompany(() => {})
     
      return true
    } catch (e) {
      setError(t("label_title_erro"), e instanceof Error ? e.message : t("label_unknown_error_description"))
      
      return false
    } finally {
      disableLoad()
    }
  }

  const handleUpdateSlug = async (
    companyId: number,
    slug: string,
    restaurantServiceType: ERestaurantServiceType,
    localServiceRadiusMeters: number,
    onSuccess: any,
  ) => {
    try {
      enableLoad()

      var response = null

      response = await PutRequest(`${API_ROUTES.COMPANY.UPDATE_SLUG}/${companyId}`, {
        slug: slug,
        restaurantServiceType: restaurantServiceType,
        localServiceRadiusMeters: localServiceRadiusMeters,
      })

      if (response.success) {
        await handleGetCompany(onSuccess)

        return true
      }

      throw new Error(response.message)
    } catch (e: any) {
      const message = e instanceof Error ? e.message : t("label_unknown_error_description")
      setError(t("label_title_erro"), message)

      return false
    } finally {
      disableLoad()
    }
  }

  const handleSaveOperatingSchedule = async (
    companyId: number,
    schedules: OperatingScheduleEntity[],
    onSuccess: any,
  ) => {
    try {
      enableLoad()

      const payload = schedules.map((s) => ({
        id: s.id,
        day: s.day,
        openTime: s.openTime,
        closeTime: s.closeTime,
        dayEnable: s.dayEnable,
      }))

      const response = await PutRequest(`${API_ROUTES.COMPANY.UPDATE_OPERATING_SCHEDULE}/${companyId}`, payload)

      if (response.success) {
        await handleGetCompany(onSuccess)
        return
      }

      throw new Error(response.message)
    } catch (e: any) {
      const message = e instanceof Error ? e.message : t("label_unknown_error_description")

      setError(t("label_title_erro"), message)
    } finally {
      disableLoad()
    }
  }

  const handleSavePublicMenuTracking = async (
    companyId: number,
    tracking: {
      publicMenuTrackingEnabled: boolean
      publicMenuGoogleTagManagerId: string
      publicMenuGoogleAnalyticsMeasurementId: string
      publicMenuGoogleAdsConversionId: string
      publicMenuMetaPixelId: string
    },
    onSuccess: any,
  ) => {
    try {
      enableLoad()

      const response = await PutRequest(`${API_ROUTES.COMPANY.UPDATE_PUBLIC_MENU_TRACKING}/${companyId}`, tracking)

      if (response.success) {
        await handleGetCompany(onSuccess)

        return true
      }

      throw new Error(response.message)
    } catch (e: any) {
      const message = e instanceof Error ? e.message : t("label_unknown_error_description")

      setError(t("label_title_erro"), message)

      return false
    } finally {
      disableLoad()
    }
  }

  const handleSavePublicMenuDesign = async (
    companyId: number,
    design: {
      publicMenuCardLayout: string
      publicMenuPrimaryColor: string
      publicMenuSecondaryColor: string
      publicMenuAccentColor: string
      publicMenuBackgroundColor: string
      publicMenuSurfaceColor: string
      publicMenuTextColor: string
      publicMenuBorderColor: string
      publicMenuButtonColor: string
      publicMenuButtonTextColor: string
      publicMenuSearchBackgroundColor: string
      publicMenuSearchBorderColor: string
      publicMenuMobileNavBackgroundColor: string
      publicMenuMobileNavActiveBackgroundColor: string
      publicMenuMobileNavActiveTextColor: string
      publicMenuMobileNavActiveIconColor: string
      publicMenuMobileNavInactiveBackgroundColor: string
      publicMenuMobileNavInactiveTextColor: string
      publicMenuMobileNavInactiveIconColor: string
      publicMenuHeaderBackgroundColor: string
      publicMenuRestaurantNameColor: string
      publicMenuRestaurantDescriptionColor: string
      publicMenuStatusCardBackgroundColor: string
      publicMenuCartButtonColor: string
      publicMenuCartBackgroundColor: string
      publicMenuCartBorderColor: string
      publicMenuCartTitleTextColor: string
      publicMenuCartDescriptionTextColor: string
      publicMenuCartTotalLabelTextColor: string
      publicMenuCartTotalValueTextColor: string
      publicMenuEmptyStateTitleColor: string
      publicMenuEmptyStateDescriptionColor: string
      publicMenuCategoryBackgroundColor: string
      publicMenuCategoryBorderColor: string
      publicMenuCategoryDividerColor: string
      publicMenuLoginButtonBackgroundColor: string
      publicMenuLoginButtonTextColor: string
      publicMenuShareButtonBackgroundColor: string
      publicMenuShareButtonTextColor: string
      publicMenuServiceSelectorBackgroundColor: string
      publicMenuServiceSelectorTextColor: string
      publicMenuServiceSelectorActiveBackgroundColor: string
      publicMenuServiceSelectorActiveTextColor: string
      publicMenuBorderThickness: number
      publicMenuHighlightPromotions: boolean
      publicMenuCompactCategories: boolean
      publicMenuUseGradientHeader: boolean
      publicMenuShowAwardsTab: boolean
      publicMenuShowPromotionsTab: boolean
      publicMenuShowExtraServicesTab: boolean
    },
    onSuccess: any,
  ) => {
    try {
      enableLoad()

      const response = await PutRequest(`${API_ROUTES.COMPANY.UPDATE_PUBLIC_MENU_DESIGN}/${companyId}`, design)

      if (response.success) {
        await handleGetCompany(onSuccess)

        return true
      }

      throw new Error(response.message)
    } catch (e: any) {
      const message = e instanceof Error ? e.message : t("label_unknown_error_description")

      setError(t("label_title_erro"), message)

      return false
    } finally {
      disableLoad()
    }
  }

  return {
    handleSaveCompany,
    handleSaveTableSelfService,
    handleSaveDefaultTable,
    handleUpdateSlug,
    handleSaveOperatingSchedule,
    handleSavePublicMenuTracking,
    handleSavePublicMenuDesign,
  }
}
